import React, { useState, useEffect, useCallback } from "react";
import {
  Users, UserPlus, Shield, Mail, Clock, CheckCircle, XCircle,
  Loader2, ChevronDown, AlertCircle, Copy, Check, Ban, UserCheck
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: "admin", label: "Administrator", desc: "Full platform access" },
  { value: "originations", label: "Originations", desc: "Deal intake and pipeline" },
  { value: "underwriter", label: "Underwriter", desc: "Deal analysis and underwriting" },
  { value: "processor", label: "Processor", desc: "Document processing" },
  { value: "lender_relations", label: "Lender Relations", desc: "Lender management" },
  { value: "investor_relations", label: "Investor Relations", desc: "Investor management" },
];

const STATUS_BADGE = {
  active:    { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Active" },
  pending:   { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending" },
  accepted:  { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Accepted" },
  revoked:   { cls: "bg-red-50 text-red-700 border-red-200", label: "Revoked" },
  expired:   { cls: "bg-gray-50 text-gray-500 border-gray-200", label: "Expired" },
  suspended: { cls: "bg-red-50 text-red-700 border-red-200", label: "Suspended" },
};

export default function TeamManagement() {
  const token = localStorage.getItem("ic_token");
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "originations", department: "" });
  const [sending, setSending] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchData = useCallback(async () => {
    try {
      const [teamRes, inviteRes] = await Promise.all([
        fetch(`${API}/api/auth/team`, { headers }),
        fetch(`${API}/api/auth/invites`, { headers }),
      ]);
      const teamData = await teamRes.json();
      const inviteData = await inviteRes.json();
      setTeam(teamData.team || []);
      setInvites(inviteData.invites || []);
    } catch (e) {
      console.error("Failed to load team data", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!form.email || !form.full_name) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/auth/invite-staff`, {
        method: "POST", headers, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send invite");
      toast.success(`Invite sent to ${form.email}`);
      setForm({ email: "", full_name: "", role: "originations", department: "" });
      setShowInviteForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (inviteId) => {
    try {
      await fetch(`${API}/api/auth/invites/${inviteId}/revoke`, { method: "POST", headers });
      toast.success("Invite revoked");
      fetchData();
    } catch {
      toast.error("Failed to revoke invite");
    }
  };

  const copyInviteLink = (invite) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}?action=accept-invite&token=${invite.token}`;
    navigator.clipboard.writeText(link);
    setCopiedId(invite.invite_id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Invite link copied");
  };

  const handleToggleStatus = async (member) => {
    const isSuspended = member.status === "suspended";
    const action = isSuspended ? "reactivate" : "suspend";
    if (!isSuspended && !window.confirm(`Are you sure you want to suspend ${member.full_name}? They will be unable to log in.`)) return;
    setTogglingId(member.user_id);
    try {
      const res = await fetch(`${API}/api/admin/users/${member.user_id}/toggle-status`, {
        method: "POST", headers, body: JSON.stringify({ reason: `Admin ${action}` }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Failed"); return; }
      toast.success(`${member.full_name} ${data.account_status === "suspended" ? "suspended" : "reactivated"}`);
      fetchData();
    } catch { toast.error("Failed to update status"); }
    finally { setTogglingId(null); }
  };

  const inputCls = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-[#0A0A0A] placeholder:text-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="team-loading">
        <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]">Team Management</h2>
          <p className="text-xs text-gray-500 mt-0.5">{team.length} team member{team.length !== 1 ? "s" : ""} &middot; {invites.filter(i => i.status === "pending").length} pending invite{invites.filter(i => i.status === "pending").length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => setShowInviteForm(!showInviteForm)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          data-testid="invite-staff-btn">
          <UserPlus className="h-3.5 w-3.5" />
          Invite Staff
        </button>
      </div>

      {/* Invite Form */}
      {showInviteForm && (
        <form onSubmit={handleInvite} className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5 space-y-4" data-testid="invite-form">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">Send Staff Invitation</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Full Name *</span>
              <input required value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                className={inputCls} placeholder="e.g. Sarah Johnson" data-testid="invite-name-input" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Email *</span>
              <input required type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className={inputCls} placeholder="sarah@company.com" data-testid="invite-email-input" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Role *</span>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                className={inputCls + " appearance-none"} data-testid="invite-role-select">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Department</span>
              <input value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))}
                className={inputCls} placeholder="e.g. Operations" data-testid="invite-dept-input" />
            </label>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowInviteForm(false)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={sending || !form.email || !form.full_name}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              data-testid="send-invite-btn">
              {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
              Send Invitation
            </button>
          </div>
        </form>
      )}

      {/* Active Team */}
      <div className="rounded-xl border border-[#EDE8DC] bg-white overflow-hidden" data-testid="team-list">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-[#0A0A0A]">Active Team ({team.length})</span>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {team.map(member => (
            <div key={member.user_id} className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50/50 transition-colors" data-testid={`team-member-${member.user_id}`}>
              <div className="h-9 w-9 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center text-sm font-bold text-emerald-700">
                {member.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0A0A0A]">{member.full_name}</div>
                <div className="text-xs text-gray-500">{member.email}</div>
              </div>
              <div className="flex items-center gap-2">
                {member.department && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
                    {member.department}
                  </span>
                )}
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {ROLES.find(r => r.value === member.role)?.label || member.role}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_BADGE[member.status]?.cls || STATUS_BADGE.active.cls}`}>
                  {STATUS_BADGE[member.status]?.label || member.status}
                </span>
                <button onClick={() => handleToggleStatus(member)} disabled={togglingId === member.user_id}
                  className={`p-1.5 rounded-lg transition-colors ${member.status === "suspended"
                    ? "text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                    : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}
                  title={member.status === "suspended" ? "Reactivate user" : "Suspend user"}
                  data-testid={`toggle-status-${member.user_id}`}>
                  {togglingId === member.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                    member.status === "suspended" ? <UserCheck className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          ))}
          {team.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-gray-400">No team members yet</div>
          )}
        </div>
      </div>

      {/* Invites */}
      {invites.length > 0 && (
        <div className="rounded-xl border border-[#EDE8DC] bg-white overflow-hidden" data-testid="invite-list">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-semibold text-[#0A0A0A]">Invitations ({invites.length})</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {invites.map(inv => {
              const badge = STATUS_BADGE[inv.status] || STATUS_BADGE.pending;
              const isPending = inv.status === "pending";
              return (
                <div key={inv.invite_id} className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50/50 transition-colors" data-testid={`invite-${inv.invite_id}`}>
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm ${
                    isPending ? "bg-amber-100 border border-amber-200 text-amber-700" :
                    inv.status === "accepted" ? "bg-emerald-100 border border-emerald-200 text-emerald-700" :
                    "bg-gray-100 border border-gray-200 text-gray-500"
                  }`}>
                    {isPending ? <Clock className="h-4 w-4" /> : inv.status === "accepted" ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0A0A0A]">{inv.full_name}</div>
                    <div className="text-xs text-gray-500">{inv.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      {ROLES.find(r => r.value === inv.role)?.label || inv.role}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badge.cls}`}>
                      {badge.label}
                    </span>
                    {isPending && (
                      <>
                        <button onClick={() => copyInviteLink(inv)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                          title="Copy invite link" data-testid={`copy-invite-${inv.invite_id}`}>
                          {copiedId === inv.invite_id ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                        <button onClick={() => handleRevoke(inv.invite_id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Revoke invite" data-testid={`revoke-invite-${inv.invite_id}`}>
                          <XCircle className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
